import { Form } from 'react-bootstrap';
import styles from './Notes.module.scss';
import Icon from '../Icon';
import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import SimpleUserCard from '../SimpleUserCard';
import { Text } from '../typography';
import { getLocalTime } from '../../utilities/time';
import { useCurrentUser } from '../../providers/UserProvider';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../providers/ModalProvider';
import { keys } from '../../utilities/translator/translation_keys';

const commentQuery = loader('./Notes.fetch.graphql');
const addCommentMutation = loader('./Notes.add.graphql');
const updateCommentMutation = loader('./Notes.update.graphql');
const deleteCommentMutation = loader('./Notes.delete.graphql');

export default function Notes({
  type,
  parentId,
  readOnly,
  setNotes = () => {},
  backgroundColor,
  preview = false,
}) {
  const { t } = useTranslation();
  const { openConfirmationModal } = useModal();
  const { user } = useCurrentUser();
  const [newComment, setNewComment] = useState({});
  const online = useOnlineStatus();
  const { refetch, data: { comments = [] } = {} } = useQuery(commentQuery, {
    skip: !type || !parentId || !online,
    variables: {
      options: {
        filters: [
          { field: 'parentId', operator: 'eq', value: `${parentId}` },
          { field: 'type', operator: 'eq', value: type },
        ],
        sort: [{ field: 'dateCreated', order: 'desc' }],
      },
    },
  });
  const [addComment] = useMutation(addCommentMutation);
  const [updateComment] = useMutation(updateCommentMutation);
  const [deleteComment] = useMutation(deleteCommentMutation);
  const [editing, setEditing] = useState(null);
  const commentRef = useRef(null);

  const handleOutsideClick = (e) => {
    if (commentRef.current && !commentRef.current.contains(e.target)) {
      setTimeout(() => {
        setEditing(null);
      }, 100);
    }
  };

  useEffect(() => {
    if (editing) document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editing]);

  useEffect(() => {
    if (comments && comments?.length > 0) {
      setNotes(comments);
    }
  }, [comments, setNotes]);

  return (
    <div className={styles.container}>
      {!readOnly && (
        <div
          className={styles.input}
          style={{ backgroundColor: backgroundColor || '' }}
        >
          <img src={user.avatarUrl} className={styles.avatar} alt="user" />
          <Form.Control
            placeholder="Leave a note.."
            as="textarea"
            rows={2}
            onChange={(e) =>
              setNewComment({ ...newComment, text: e.target.value })
            }
            value={newComment.text || ''}
          />
          <Icon
            hover
            className={styles.transparent}
            disabled={!!!newComment.text}
            onClick={() =>
              !preview &&
              addComment({
                variables: { ...newComment, type, parentId },
              }).then(() => {
                setNewComment({});
                refetch();
              })
            }
            color="blue"
          >
            send
          </Icon>
        </div>
      )}
      <div className={styles.comments}>
        {comments?.map(({ creator, text, dateCreated, dateModified, id }) => (
          <div key={`note-${id}`}>
            <div className={styles.card}>
              <div className={styles.flexRow}>
                <SimpleUserCard mediumAvatar user={creator} />
                <Text size="sm" noMargin color="secondary">
                  {`${getLocalTime(dateCreated).format(
                    'dddd MMMM Do YYYY HH:mm',
                  )} ${
                    dateModified !== dateCreated
                      ? `(${t(keys.common.EDITED).toLowerCase()})`
                      : ''
                  }`}
                </Text>
              </div>
              {creator?.id === user?.id && (
                <div>
                  <Icon
                    className={styles.icon}
                    style={{ fontSize: '1rem' }}
                    color={editing === id ? 'green' : null}
                    onClick={() => setEditing(editing ? null : id)}
                  >
                    {editing === id ? 'check' : 'edit'}
                  </Icon>
                  <Icon
                    className={styles.icon}
                    style={{ fontSize: '1rem' }}
                    color="red"
                    onClick={() =>
                      openConfirmationModal({
                        variant: 'warning',
                        title: t(keys.action.DELETE_VARIABLE, {
                          variable: t(keys.common.THIS_COMMENT),
                        }),
                        description: t(keys.action.DELETE_CONFIRMATION, {
                          variable: t(keys.common.THIS_COMMENT),
                        }),
                        onSubmit: () =>
                          deleteComment({
                            variables: { commentId: id },
                          }).then(() => refetch()),
                      })
                    }
                  >
                    delete
                  </Icon>
                </div>
              )}
            </div>
            <div className={styles.content}>
              {editing === id ? (
                <Form.Control
                  ref={commentRef}
                  defaultValue={text}
                  onBlur={(e) =>
                    updateComment({
                      variables: {
                        commentId: id,
                        text: e.target.value,
                      },
                    })
                  }
                />
              ) : (
                <Text size="md" prewrap noMargin>
                  {text}
                </Text>
              )}
            </div>
            <div className={styles.spacer} />
          </div>
        ))}
      </div>
    </div>
  );
}
